<template>
  <Dialog
    v-model:visible="visible"
    :header="dialogTitle"
    :modal="true"
    :closable="true"
    :draggable="false"
    :style="{ width: '672px' }"
    :pt="{ root: { style: 'max-height: 90vh; overflow-y: auto;' }, header: { style: 'border-bottom: 1px solid var(--p-surface-700); margin-bottom: 1rem;' } }"
    @hide="$emit('close')"
  >

    <!-- STEP 1: Connessione -->
    <div v-if="step === 'connect'" class="flex flex-col gap-4">

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium">{{ $t('botEditor.tools.httpMcpModal.nameLabel') }} *</label>
        <InputText id="input-http-mcp-name" v-model="form.name" :placeholder="$t('botEditor.tools.httpMcpModal.namePlaceholder')" class="w-full" />
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium">{{ $t('botEditor.tools.httpMcpModal.urlLabel') }} *</label>
        <InputText id="input-http-mcp-url" v-model="form.url" type="url" :placeholder="$t('botEditor.tools.httpMcpModal.urlPlaceholder')" class="w-full" />
      </div>

      <!-- Autenticazione -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium">{{ $t('botEditor.tools.httpMcpModal.authLabel') }}</label>
        <Select
          v-model="form.authType"
          :options="authTypeOptions"
          option-label="label"
          option-value="value"
          class="w-full"
        />

        <div v-if="form.authType === 'bearer'" class="flex flex-col gap-1">
          <label class="text-xs">{{ $t('botEditor.tools.httpMcpModal.tokenLabel') }}</label>
          <InputText id="input-http-mcp-token" v-model="form.authValue" type="password" :placeholder="$t('botEditor.tools.httpMcpModal.tokenPlaceholder')" class="w-full" />
        </div>

        <div v-if="form.authType === 'api_key'" class="flex flex-col gap-2">
          <div class="flex gap-2">
            <div class="flex flex-col gap-1 flex-1">
              <label class="text-xs">{{ $t('botEditor.tools.httpMcpModal.headerNameLabel') }}</label>
              <InputText v-model="form.authHeaderName" :placeholder="$t('botEditor.tools.httpMcpModal.headerNamePlaceholder')" class="w-full" />
            </div>
            <div class="flex flex-col gap-1 flex-1">
              <label class="text-xs">{{ $t('botEditor.tools.httpMcpModal.valueLabel') }}</label>
              <InputText v-model="form.authValue" type="password" :placeholder="$t('botEditor.tools.httpMcpModal.apiKeyPlaceholder')" class="w-full" />
            </div>
          </div>
        </div>
      </div>

      <!-- Headers avanzati (collapsible) -->
      <div>
        <Button id="btn-toggle-advanced-headers" severity="secondary" text @click="showAdvancedHeaders = !showAdvancedHeaders">
          <span class="material-icons" style="font-size:16px">{{ showAdvancedHeaders ? 'expand_less' : 'expand_more' }}</span>
          {{ $t('botEditor.tools.httpMcpModal.advancedHeaders') }}
        </Button>

        <div v-if="showAdvancedHeaders" class="mt-3 flex flex-col gap-2">
          <div v-for="(header, idx) in form.customHeaders" :key="idx" class="flex gap-2 items-center">
            <InputText v-model="header.key" :placeholder="$t('botEditor.tools.httpMcpModal.headerNameLabel')" class="flex-1" />
            <InputText v-model="header.value" :placeholder="$t('botEditor.tools.httpMcpModal.headerValuePlaceholder')" class="flex-1" />
            <Button data-telemetry-id="btn-remove-custom-header" severity="danger" text @click="removeHeader(idx)">
              <span class="material-icons" style="font-size:18px">remove_circle</span>
            </Button>
          </div>
          <Button id="btn-add-custom-header" severity="secondary" text @click="addHeader">
            <span class="material-icons" style="font-size:16px">add_circle</span>
            {{ $t('botEditor.tools.httpMcpModal.addHeader') }}
          </Button>
        </div>
      </div>

      <div v-if="connectError" class="bg-red-900/20 border border-red-500 text-red-300 px-4 py-3 rounded text-sm">
        {{ connectError }}
      </div>

      <div class="flex justify-end gap-3 mt-2">
        <Button id="btn-cancel-http-mcp" severity="secondary" @click="$emit('close')">{{ $t('botEditor.tools.httpMcpModal.cancelButton') }}</Button>
        <Button
          id="btn-connect-http-mcp"
          severity="primary"
          :label="connecting ? $t('botEditor.tools.httpMcpModal.connectingButton') : $t('botEditor.tools.httpMcpModal.connectButton')"
          :loading="connecting"
          :disabled="connecting || !form.url || !form.name"
          @click="discover"
        />
      </div>
    </div>

    <!-- STEP 2: Conferma tool scoperti -->
    <div v-if="step === 'confirm'" class="flex flex-col gap-4">
      <p class="text-sm">
        {{ $t('botEditor.tools.httpMcpModal.toolsAvailable', { count: discoveredTools.length, name: form.name }) }}
      </p>

      <div class="flex flex-col gap-2 max-h-64 overflow-y-auto">
        <div
          v-for="tool in discoveredTools"
          :key="tool.name"
          class="flex flex-col p-3 rounded"
          style="background: var(--p-surface-700);"
        >
          <span class="font-mono text-sm" style="color: var(--p-primary-300);">{{ tool.name }}</span>
          <span class="text-xs mt-1" style="color: var(--p-text-muted-color);">{{ tool.description }}</span>
        </div>
      </div>

      <div v-if="saveError" class="bg-red-900/20 border border-red-500 text-red-300 px-4 py-3 rounded text-sm">
        {{ saveError }}
      </div>

      <div class="flex justify-end gap-3 mt-2">
        <Button id="btn-back-to-http-mcp-connect" severity="secondary" @click="step = 'connect'">{{ $t('botEditor.tools.httpMcpModal.backButton') }}</Button>
        <Button
          id="btn-save-http-mcp"
          severity="success"
          :label="saving ? $t('botEditor.tools.httpMcpModal.savingButton') : $t('botEditor.tools.httpMcpModal.addServerButton')"
          :loading="saving"
          :disabled="saving"
          @click="save"
        />
      </div>
    </div>

  </Dialog>
</template>

<script setup>
/**
 * HttpMcpConfigModal.vue
 *
 * Two-step modal for connecting an HTTP MCP server to a bot.
 * Step 1 ("connect"): collects server name, URL, auth config, and optional custom headers,
 * then calls the discovery endpoint.
 * Step 2 ("confirm"): shows the discovered tools and lets the user save the server.
 *
 * @example
 * <HttpMcpConfigModal :botId="bot.id" @close="showModal = false" @server-added="onServerAdded" />
 */
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '../../services/api';

const props = defineProps({
  /** The ID of the bot this MCP server will be attached to. */
  botId: { type: String, required: true },
});

const emit = defineEmits(['close', 'server-added']);

const { t } = useI18n();

/** Controls Dialog visibility; starts open and emits 'close' on hide. */
const visible = ref(true);

const step = ref('connect');
const connecting = ref(false);
const saving = ref(false);
const connectError = ref('');
const saveError = ref('');
const discoveredTools = ref([]);
const showAdvancedHeaders = ref(false);

const form = ref({
  name: '',
  url: '',
  authType: 'none',
  authHeaderName: '',
  authValue: '',
  customHeaders: [],
});

/** Options for the auth type Select, localised at render time. */
const authTypeOptions = computed(() => [
  { label: t('botEditor.tools.httpMcpModal.authNone'),   value: 'none' },
  { label: t('botEditor.tools.httpMcpModal.authBearer'), value: 'bearer' },
  { label: t('botEditor.tools.httpMcpModal.authApiKey'), value: 'api_key' },
]);

/** Dialog header text, driven by the current step. */
const dialogTitle = computed(() =>
  step.value === 'connect'
    ? t('botEditor.tools.httpMcpModal.connectTitle')
    : t('botEditor.tools.httpMcpModal.toolsTitle')
);

function addHeader() {
  form.value.customHeaders.push({ key: '', value: '' });
}

function removeHeader(idx) {
  form.value.customHeaders.splice(idx, 1);
}

function buildPayload() {
  return {
    url: form.value.url,
    authConfig: {
      type: form.value.authType,
      headerName: form.value.authHeaderName,
      value: form.value.authValue,
    },
    customHeaders: form.value.customHeaders.filter(h => h.key && h.value),
  };
}

async function discover() {
  if (!form.value.url || !form.value.name) return;
  connecting.value = true;
  connectError.value = '';

  try {
    const res = await api.post('/mcp/discover', buildPayload());
    discoveredTools.value = res.data.tools || [];
    step.value = 'confirm';
  } catch (err) {
    connectError.value = err.response?.data?.error?.message || t('botEditor.tools.httpMcpModal.connectError');
  } finally {
    connecting.value = false;
  }
}

async function save() {
  saving.value = true;
  saveError.value = '';

  const { authConfig, customHeaders } = buildPayload();

  try {
    await api.post('/mcp/custom', {
      botId: props.botId,
      name: form.value.name,
      endpointUrl: form.value.url,
      authConfig,
      customHeaders,
    });
    emit('server-added');
  } catch (err) {
    saveError.value = err.response?.data?.error?.message || t('botEditor.tools.httpMcpModal.saveError');
  } finally {
    saving.value = false;
  }
}
</script>
